import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DoboxConfigSource,
  DoboxRuleApplyTarget,
  InboxEntry,
} from '@this+that/models';

import { useDeleteInboxEntry } from './useDeleteInboxEntry';
import { pushGTMEvent } from '../../utils/analytics/gtm';
import { resolveApplyTarget, useDoBoxConfigUpdate } from '../DoBox';
import { useCustomToast } from '../useCustomToast';

interface UseDoBoxFeedbackReturn {
  submitFeedbackAndDelete: (
    inboxEntry: InboxEntry,
    feedback: string,
  ) => Promise<boolean>;
  isSubmitting: boolean;
  feedbackError: Error | null;
}

export function useDoBoxFeedback(): UseDoBoxFeedbackReturn {
  const { t } = useTranslation();
  const { successMessage, errorMessage } = useCustomToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<Error | null>(null);

  const { deleteInboxEntry } = useDeleteInboxEntry();
  const { updateInstructions } = useDoBoxConfigUpdate();

  const submitFeedbackAndDelete = useCallback(
    async (inboxEntry: InboxEntry, feedback: string): Promise<boolean> => {
      setIsSubmitting(true);
      setFeedbackError(null);

      try {
        const configUpdateSuccess = await updateInstructions(
          inboxEntry,
          feedback,
        );

        if (!configUpdateSuccess) {
          throw new Error('Failed to update DoBox instructions');
        }

        const deleteSuccess = await deleteInboxEntry(inboxEntry.id);

        if (!deleteSuccess) {
          throw new Error(
            'Failed to delete inbox entry after feedback submission',
          );
        }

        const source =
          resolveApplyTarget(inboxEntry) === DoboxRuleApplyTarget.EMAIL
            ? DoboxConfigSource.EMAIL
            : DoboxConfigSource.CHAT;

        pushGTMEvent({
          event: 'dobox_feedback_submitted',
          rule_source: source,
          feedback_length: feedback.length,
        });
        pushGTMEvent({
          event: 'dobox_rule_created',
          rule_source: source,
          feedback_length: feedback.length,
        });
        successMessage(t('doBox.rejectionDialog.successMessage'));
        return true;
      } catch (error) {
        const errorInstance =
          error instanceof Error
            ? error
            : new Error('Failed to submit feedback and delete entry');

        setFeedbackError(errorInstance);
        errorMessage(t('doBox.rejectionDialog.errorMessage'));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [deleteInboxEntry, updateInstructions, successMessage, errorMessage, t],
  );

  return {
    submitFeedbackAndDelete,
    isSubmitting,
    feedbackError,
  };
}
